import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BASE_API_URL } from '../apiConfig';
import ConfirmationModal from '../components/ConfirmationModal';
import { useTranslation } from 'react-i18next';

const LeaguePlayerLinks = () => {
    const { t } = useTranslation();
    const { leagueId } = useParams();
    const navigate = useNavigate();
    const [links, setLinks] = useState([]);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 480);

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [linkToDelete, setLinkToDelete] = useState(null);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 480);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Form state: ora usiamo gli oggetti giocatore per l'autocomplete
    const [p1Search, setP1Search] = useState('');
    const [p1Selected, setP1Selected] = useState(null);
    const [p1ShowResults, setP1ShowResults] = useState(false);

    const [p2Search, setP2Search] = useState('');
    const [p2Selected, setP2Selected] = useState(null);
    const [p2ShowResults, setP2ShowResults] = useState(false);

    const [type, setType] = useState('together');

    const loadData = async () => {
        setLoading(true);
        const token = localStorage.getItem('token');
        try {
            // Get role info to determine admin status

            const roleRes = await fetch(`${BASE_API_URL}/leagues/${leagueId}/role`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const roleData = await roleRes.json();
            setIsAdmin(roleData.is_admin);

            // Get links
            const linksRes = await fetch(`${BASE_API_URL}/leagues/${leagueId}/player-links`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const linksData = await linksRes.json();
            setLinks(linksData);

            // Get members
            const membersRes = await fetch(`${BASE_API_URL}/leagues/${leagueId}/members`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const membersData = await membersRes.json();
            setMembers(membersData);

        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [leagueId]);

    const handleCreateLink = async (e) => {
        e.preventDefault();
        if (!p1Selected || !p2Selected) return;

        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${BASE_API_URL}/leagues/${leagueId}/player-links`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    player1_id: p1Selected.id,
                    player2_id: p2Selected.id,
                    link_type: type
                })
            });
            if (res.ok) {
                setP1Search(''); setP1Selected(null);
                setP2Search(''); setP2Selected(null);
                loadData();
            } else {
                const data = await res.json();
                alert(data.error || t('error'));
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleDeleteLink = (link) => {
        setLinkToDelete(link);
        setIsDeleteModalOpen(true);
    };

    const confirmDeleteLink = async () => {
        if (!linkToDelete) return;
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${BASE_API_URL}/leagues/${leagueId}/player-links/${linkToDelete.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                setIsDeleteModalOpen(false);
                setLinkToDelete(null);
                loadData();
            }
        } catch (err) {
            console.error(err);
        }
    };

    if (loading) return <div className="loading">{t('loading')}</div>;

    // Filtra membri per autocomplete
    const filteredP1 = members.filter(m =>
        (m.first_name + ' ' + m.last_name).toLowerCase().includes(p1Search.toLowerCase())
    );
    const filteredP2 = members.filter(m =>
        m.id !== p1Selected?.id &&
        (m.first_name + ' ' + m.last_name).toLowerCase().includes(p2Search.toLowerCase())
    );

    return (
        <div className="layout">
            <main className="container page-content">
                <button
                    onClick={() => navigate(`/league/${leagueId}/members`)}
                    className="secondary-btn outline"
                    style={{ marginBottom: '20px', fontSize: '0.85rem' }}
                >
                    ⬅ {t('back_to_participants')}
                </button>

                <div className="section-header-box" style={{ marginBottom: '30px' }}>
                    <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '0 0 10px 0' }}>
                        🔗 {t('player_links')}
                    </h2>
                    <p className="description-text" style={{ textAlign: 'left', color: '#666', fontSize: '0.9rem' }}>
                        {t('player_links_desc')}
                    </p>
                </div>

                {isAdmin && (
                    <div className="link-builder-card">
                        <div className="link-builder-header">
                            ➕ {isMobile ? t('add_link') : t('create_new_link_autocomplete')}
                        </div>

                        <form onSubmit={handleCreateLink} className="link-builder-form">
                            <div className="link-builder-row">

                                {/* Giocatore 1 Autocomplete */}
                                <div className="autocomplete-wrapper">
                                    <div className="form-label-small">{t('player_1')}</div>
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder={t('player_name_placeholder')}
                                        value={p1Search}
                                        onChange={e => { setP1Search(e.target.value); setP1ShowResults(true); if (p1Selected) setP1Selected(null); }}
                                        onFocus={() => setP1ShowResults(true)}
                                    />
                                    {p1ShowResults && p1Search.length > 0 && !p1Selected && (
                                        <div className="autocomplete-results">
                                            {filteredP1.length > 0 ? filteredP1.map(m => (
                                                <div key={m.id} className="autocomplete-item" onClick={() => { setP1Selected(m); setP1Search(m.first_name + ' ' + m.last_name); setP1ShowResults(false); }}>
                                                    👤 {m.first_name} {m.last_name}
                                                </div>
                                            )) : <div className="autocomplete-item no-results">{t('no_results')}</div>}
                                        </div>
                                    )}
                                </div>

                                {/* Tipo Legame - Slider Selector */}
                                <div className="relation-container">
                                    <div className="relation-toggle-group">
                                        {/* Sfondo scorrevole */}
                                        <div className={`relation-slider-bg ${type}`}></div>

                                        <button
                                            type="button"
                                            onClick={() => setType('together')}
                                            className={`relation-btn ${type === 'together' ? 'active' : ''}`}
                                        >
                                            🤝 {isMobile ? "" : t('together')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setType('separate')}
                                            className={`relation-btn ${type === 'separate' ? 'active' : ''}`}
                                        >
                                            ⚔️ {isMobile ? "" : t('separate')}
                                        </button>
                                    </div>
                                </div>

                                {/* Giocatore 2 Autocomplete */}
                                <div className="autocomplete-wrapper">
                                    <div className="form-label-small">{t('player_2')}</div>
                                    <input
                                        type="text"
                                        className="form-input"
                                        placeholder={t('player_name_placeholder')}
                                        disabled={!p1Selected}
                                        value={p2Search}
                                        onChange={e => { setP2Search(e.target.value); setP2ShowResults(true); if (p2Selected) setP2Selected(null); }}
                                        onFocus={() => setP2ShowResults(true)}
                                    />
                                    {p2ShowResults && p2Search.length > 0 && !p2Selected && (
                                        <div className="autocomplete-results">
                                            {filteredP2.length > 0 ? filteredP2.map(m => (
                                                <div key={m.id} className="autocomplete-item" onClick={() => { setP2Selected(m); setP2Search(m.first_name + ' ' + m.last_name); setP2ShowResults(false); }}>
                                                    👤 {m.first_name} {m.last_name}
                                                </div>
                                            )) : <div className="autocomplete-item no-results">{t('no_results')}</div>}
                                        </div>
                                    )}
                                </div>

                                <button
                                    type="submit"
                                    className="primary-btn outline"
                                    disabled={!p1Selected || !p2Selected}
                                >
                                    ✅ {t('save')}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                <div className="links-section">
                    <h3>{t('active_links')} ({links.length})</h3>
                    {links.length === 0 ? (
                        <div className="empty-links-banner">
                            {t('no_links_defined')}
                        </div>
                    ) : (
                        <div className="links-grid">
                            {links.map(link => (
                                <div key={link.id} className={`link-card ${link.link_type}`}>
                                    <div className="link-card-content">
                                        <div className="link-avatar-box">
                                            <div className="link-avatar-circle">
                                                {link.u1_avatar ? <img src={link.u1_avatar} alt="u1" /> : <span>👤</span>}
                                            </div>
                                            <div className="link-player-name">{link.u1_name}</div>
                                        </div>

                                        <div className="link-arrow-icon">
                                            {link.link_type === 'together' ? '🤝' : '⚔️'}
                                        </div>

                                        <div className="link-avatar-box">
                                            <div className="link-avatar-circle">
                                                {link.u2_avatar ? <img src={link.u2_avatar} alt="u2" /> : <span>👤</span>}
                                            </div>
                                            <div className="link-player-name">{link.u2_name}</div>
                                        </div>

                                        {isAdmin && (
                                            <button onClick={() => handleDeleteLink(link)} className="danger-btn outline" title={t('remove_link')}>
                                                🗑️
                                            </button>
                                        )}
                                    </div>
                                    <div className="link-description-text">
                                        {link.link_type === 'together' ? t('always_same_team') : t('always_opponent_teams')}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Modale Conferma Cancellazione Legame */}
                <ConfirmationModal
                    isOpen={isDeleteModalOpen}
                    onClose={() => { setIsDeleteModalOpen(false); setLinkToDelete(null); }}
                    onConfirm={confirmDeleteLink}
                    title={`${t('remove_link')}?`}
                    message={linkToDelete ? t('remove_link_ask') : ""}
                />
            </main>
        </div>
    );
};

export default LeaguePlayerLinks;
